import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Paper } from 'features/app/components/Paper';
import { useStyles } from 'features/subscription/components/MyPlanDetails/style';
import { Grid, Typography, List, ListItem } from '@material-ui/core';
import BaseButton from 'features/app/components/BaseButton';
import { redirectCheckoutSession, redirectPortalSession } from 'features/subscription/api';
import { useTranslation } from 'hooks/useTranslation';
import { useSelector } from 'store';
import { useSubscription } from 'features/user/hooks/useSubscription';
import { getAvailableActionsWithSubscriptionPlan } from 'features/subscription/utils';
import { profileRequest } from 'features/user/redux/actions';
import { useHistory, useLocation } from 'react-router-dom';
import { getQueryParamFromSearch } from 'utils';
import { useDispatch } from 'react-redux';

type PlanDetailsProps = {
  subscriptionButtonDisabled: boolean;
  setSubscriptionButtonDisabled: Dispatch<SetStateAction<boolean>>;
};

export const MyPlanDetails: React.FC<PlanDetailsProps> = ({
  subscriptionButtonDisabled,
  setSubscriptionButtonDisabled,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('subscription');
  const { search } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const subscriptionStatus = useMemo(() => getQueryParamFromSearch(search, 'subscription'), [search]);
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const { selectedPlans, allSubscriptionData } = useSelector(state => state?.subscriptionReducer);
  const { subscription, subscriptionPlan, isSubscriptionActive } = useSubscription();
  const premiumSubscriptionData = allSubscriptionData.find(({ name }) => name === 'Premium');
  const selectedPremiumPlan = premiumSubscriptionData?.prices.filter(
    (data: { recurring_interval: string }) => data?.recurring_interval === selectedPlans
  )?.[0];

  const selectedPlan =
    subscription &&
    allSubscriptionData
      .find(({ name }) => name.toUpperCase() === subscriptionPlan)
      ?.prices.find(({ recurring_interval }) => {
        switch (recurring_interval) {
          case 'month':
            return subscription.recurring_interval === 'MONTHLY';
          case 'year':
            return subscription.recurring_interval === 'YEARLY';
        }
      });

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined = undefined;

    if (
      subscriptionStatus == 'created' &&
      !isSubscriptionActive &&
      !['past_due', 'unpaid', 'incomplete_expired'].includes(subscription?.subscription_status || '')
    ) {
      intervalId = setInterval(() => {
        dispatch(profileRequest({ useLoading: false }));
      }, 3000);
    } else if (isSubscriptionActive) {
      queryParams.delete('subscription');
      history.replace({
        search: queryParams.toString(),
      });
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [subscriptionStatus, subscription?.subscription_status, dispatch, isSubscriptionActive, queryParams, history]);

  const handleCheckoutPortal = () => {
    if (subscription) {
      setSubscriptionButtonDisabled(true);
      redirectPortalSession()
        .then(res => {
          window.location.assign(res.data.url);
        })
        .catch(() => setSubscriptionButtonDisabled(false));
    } else if (selectedPremiumPlan?.id) {
      setSubscriptionButtonDisabled(true);
      redirectCheckoutSession(selectedPremiumPlan.id)
        .then(res => {
          window.location.assign(res.data.url);
        })
        .catch(() => {
          setSubscriptionButtonDisabled(false);
        });
    }
  };

  const planName = useMemo(() => {
    switch (subscription?.subscription_plan) {
      case 'BASIC':
        return t('plans.basic');
      case 'PREMIUM':
        return t('plans.premium');
      case 'ENTERPRISE':
        return t('plans.enterprise');
      default:
        return t('plans.free');
    }
  }, [subscription, t]);

  const priceText = useMemo(() => {
    if (selectedPlan) {
      return `${selectedPlan.unit_amount / 100} ${selectedPlan.currency.toUpperCase()} / ${
        selectedPlan.recurring_interval === 'month' ? t('month') : t('year')
      }`;
    } else if (!subscription) {
      return `$0.00 / ${t('month')}`;
    } else if (subscription.subscription_plan === 'ENTERPRISE') {
      return subscription.recurring_interval === 'MONTHLY' ? t('month') : t('year');
    }
    return '';
  }, [selectedPlan, subscription, t]);

  return (
    <Grid container>
      <Grid item>
        <Paper className={classes.root}>
          <Grid container spacing={3} className={classes.wrapperBlocks}>
            <Grid item className={classes.overallPlanDescription}>
              <div className={classes.planText}>
                <Typography component="h4" variant="h4">
                  {t('yourPlan')}
                </Typography>
                <Typography component="h2" variant="h2">
                  {planName}
                </Typography>
                <Typography component="p" variant="body2" className={classes.priceText}>
                  {priceText}
                </Typography>
              </div>
            </Grid>
            <Grid item md={5}>
              <List className={classes.planListData} disablePadding>
                {getAvailableActionsWithSubscriptionPlan(subscription, t).map((data, i) => {
                  return (
                    <ListItem disableGutters key={i} className={`${!data?.available ? classes.notAvailable : ''}`}>
                      <div>{data.icon}</div> {data.title}
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            {subscriptionPlan != 'ENTERPRISE' && (
              <Grid item md={4}>
                <BaseButton
                  onClick={handleCheckoutPortal}
                  disabled={subscriptionButtonDisabled}
                  className={classes.button}
                >
                  {subscription ? t('manageSubscriptions') : t('noSubscription.subscribeNow')}
                </BaseButton>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
